import React, { useEffect, useState, useMemo, useRef } from 'react';
import './ConversationPanel.scss';
import { useAxios } from 'hooks/useAxios';
import { getDataFromSingleEndpoint } from 'services/Torogoz/TogorozApi';
import { useStore as useGlobalStore } from 'store/useGlobalStore';
import {
  TranscriptMessage,
  SessionData,
  SentimentOption,
  CategoryFilterItem,
} from '../shared/types';
import { getCategoryColor, getCategoryDisplayName } from '../shared/categoryColors';
import MessageBubble from './MessageBubble/MessageBubble';
import ConversationFilter from './ConversationFilter/ConversationFilter';
import ChatTranscriptSkeleton from 'components/ChatTranscript/ChatTranscriptSkeleton';

interface ConversationPanelProps {
  sessionId: string | null;
  isExternal: boolean;
  onSessionLoaded?: (date: string, messageCount: number) => void;
  // Controlled filter state (lifted so the filter can also render in the
  // sticky session header when the inline one scrolls out of view).
  categoryFilters: string[];
  sentimentFilter: SentimentOption;
  filterOpen: boolean;
  onCategoryFiltersChange: (filters: string[]) => void;
  onSentimentFilterChange: (filter: SentimentOption) => void;
  onFilterToggle: () => void;
  onClear: () => void;
  onFilterItemsChange: (items: CategoryFilterItem[]) => void;
  onInlineFilterVisibility: (visible: boolean) => void;
}

function isGroupFirst(messages: TranscriptMessage[], index: number): boolean {
  if (index === 0) return true;
  const previous = messages[index - 1];
  const current = messages[index];
  const previousIdentity = previous.chatFields?.emitter;
  const currentIdentity = current.chatFields?.emitter;

  return (
    previous.emitterRole !== current.emitterRole ||
    (previousIdentity !== undefined &&
      currentIdentity !== undefined &&
      previousIdentity !== currentIdentity)
  );
}

const ConversationPanel: React.FC<ConversationPanelProps> = ({
  sessionId,
  isExternal,
  onSessionLoaded,
  categoryFilters,
  sentimentFilter,
  filterOpen,
  onCategoryFiltersChange,
  onSentimentFilterChange,
  onFilterToggle,
  onClear,
  onFilterItemsChange,
  onInlineFilterVisibility,
}) => {
  const { endpoints, dateFilter } = useGlobalStore((state) => ({
    endpoints: state.endpoints,
    dateFilter: state.dateFilter,
  }));

  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [systemMessages, setSystemMessages] = useState<TranscriptMessage[]>([]);
  const [chatMessages, setChatMessages] = useState<TranscriptMessage[]>([]);

  const inlineFilterRef = useRef<HTMLDivElement>(null);

  const { response: transcriptResponse, axiosFetch, loading } = useAxios();

  useEffect(() => {
    if (!sessionId) return;
    setSessionData(null);

    getDataFromSingleEndpoint(
      endpoints.getWidgetData,
      {
        filters: { session_id: sessionId, date_interval: dateFilter },
        source: 'chat',
        actionName: 'session-transcript',
        ...(isExternal
          ? { external: true }
          : { external: false, mockEndpoint: 'data-session-transcript' }),
      },
      axiosFetch
    );
  }, [sessionId, isExternal]);

  useEffect(() => {
    if (!transcriptResponse?.data) return;
    const data: SessionData = transcriptResponse.data;
    setSessionData(data);

    const transcript: TranscriptMessage[] = Array.isArray(data.transcript) ? data.transcript : [];
    setSystemMessages(transcript.filter((m) => m.emitterRole === 'System'));
    setChatMessages(transcript.filter((m) => m.emitterRole !== 'System'));

    onSessionLoaded?.(
      data.date ?? '',
      transcript.filter((message) => message.emitterRole !== 'System').length
    );
  }, [transcriptResponse]);

  const categoryFilterItems = useMemo((): CategoryFilterItem[] => {
    if (!chatMessages.length) return [];
    const counts: Record<string, number> = {};
    chatMessages.forEach((m) => {
      const cat = m.categories?.[0];
      const key = cat?.name ?? cat?.id ?? 'Uncategorized';
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return Object.entries(counts).map(([key, count]) => ({
      key,
      displayName: getCategoryDisplayName(key),
      count,
      colorSpec: getCategoryColor(key),
    }));
  }, [chatMessages]);

  const allMessages = useMemo(
    (): TranscriptMessage[] => [...systemMessages, ...chatMessages],
    [systemMessages, chatMessages]
  );

  const filteredMessages = useMemo((): TranscriptMessage[] => {
    if (categoryFilters.length === 0 && sentimentFilter === 'all') return allMessages;

    return allMessages.filter((m) => {
      if (m.emitterRole === 'System') return true;

      const catKey = m.categories?.[0]?.name ?? m.categories?.[0]?.id ?? 'Uncategorized';
      if (categoryFilters.length > 0 && !categoryFilters.includes(catKey)) return false;

      if (sentimentFilter !== 'all') {
        const sent = (m.sentimentCategory ?? '').toLowerCase();
        if (sentimentFilter === 'positive' && sent !== 'positive') return false;
        if (sentimentFilter === 'negative' && sent !== 'negative') return false;
      }

      return true;
    });
  }, [allMessages, categoryFilters, sentimentFilter]);

  // Report the available category filter items up so the sticky-header filter
  // can render the same options.
  useEffect(() => {
    onFilterItemsChange(categoryFilterItems);
  }, [categoryFilterItems, onFilterItemsChange]);

  // Watch whether the inline filter is visible within the scroll area; when it
  // scrolls out, the parent shows the filter in the sticky session header.
  useEffect(() => {
    const el = inlineFilterRef.current;
    if (!el) return;
    const root = el.closest('.ca-workspace__center-scroll') as HTMLElement | null;
    const observer = new IntersectionObserver(
      ([entry]) => onInlineFilterVisibility(entry.isIntersecting),
      { root, threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sessionData, onInlineFilterVisibility]);

  if (!sessionId) {
    return (
      <div className="ca-conv-panel ca-conv-panel--empty" aria-label="No session selected">
        <span className="material-icons ca-conv-panel__empty-icon">forum</span>
        Select a session to view the conversation
      </div>
    );
  }

  if (loading || !sessionData) {
    return <ChatTranscriptSkeleton />;
  }

  const nonSystemFiltered = filteredMessages.filter((m) => m.emitterRole !== 'System');
  const hasFilters = categoryFilters.length > 0 || sentimentFilter !== 'all';
  const messageCountLabel = hasFilters
    ? `${nonSystemFiltered.length} of ${chatMessages.length} messages`
    : `${chatMessages.length} messages`;

  return (
    <div className="ca-conv-panel">
      <div className="ca-conv-panel__header">
        <h2 className="ca-conv-panel__title">Conversation</h2>
        <span className="ca-conv-panel__count">{messageCountLabel}</span>
        <div className="ca-conv-panel__filter-slot" ref={inlineFilterRef}>
          <ConversationFilter
            categories={categoryFilterItems}
            categoryFilters={categoryFilters}
            sentimentFilter={sentimentFilter}
            onCategoryFiltersChange={onCategoryFiltersChange}
            onSentimentFilterChange={onSentimentFilterChange}
            onClear={onClear}
            isOpen={filterOpen}
            onToggle={onFilterToggle}
          />
        </div>
      </div>

      {hasFilters && categoryFilters.length > 0 && (
        <div className="ca-conv-panel__chips" role="list" aria-label="Active filters">
          {categoryFilters.map((key) => {
            const item = categoryFilterItems.find((c) => c.key === key);
            if (!item) return null;
            return (
              <span
                key={key}
                className="ca-conv-panel__chip"
                role="listitem"
                style={{
                  background: item.colorSpec.bg,
                  color: item.colorSpec.color,
                  borderColor: item.colorSpec.border,
                }}
              >
                {item.displayName}
                <button
                  onClick={() => onCategoryFiltersChange(categoryFilters.filter((k) => k !== key))}
                  aria-label={`Remove ${item.displayName} filter`}
                  className="ca-conv-panel__chip-remove"
                  type="button"
                >
                  <span className="material-icons" aria-hidden="true">
                    close
                  </span>
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="ca-conv-panel__messages" role="list" aria-label="Conversation messages">
        {filteredMessages.length === 0 ? (
          <div className="ca-conv-panel__empty-filter">
            <span className="material-icons ca-conv-panel__empty-icon">filter_alt_off</span>
            No messages match the selected filters.
          </div>
        ) : (
          filteredMessages.map((message, index) => (
            <div
              key={message.id ?? index}
              style={{
                marginTop:
                  index === 0 ? '0' : isGroupFirst(filteredMessages, index) ? '20px' : '4px',
              }}
            >
              <MessageBubble
                message={message}
                firstInGroup={isGroupFirst(filteredMessages, index)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationPanel;
